import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";
const CookieConsent = lazy(() => import("./components/CookieConsent.tsx"));
import ScrollToTop from "./components/ScrollToTop.tsx";

import { useImageProtection } from "./hooks/useImageProtection";

// Topic & supporting pages — code-split per route
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const Beaches = lazy(() => import("./pages/Beaches.tsx"));
const BeachDetail = lazy(() => import("./pages/BeachDetail.tsx"));
const Surf = lazy(() => import("./pages/Surf.tsx"));
const Tides = lazy(() => import("./pages/Tides.tsx"));
const Weather = lazy(() => import("./pages/Weather.tsx"));
const Dining = lazy(() => import("./pages/Dining.tsx"));
const Nightlife = lazy(() => import("./pages/Nightlife.tsx"));
const Activities = lazy(() => import("./pages/Activities.tsx"));
const Trails = lazy(() => import("./pages/Trails.tsx"));
const TrailDetail = lazy(() => import("./pages/TrailDetail.tsx"));
const TorreyTrailsIndex = lazy(() => import("./pages/TorreyTrailsIndex.tsx"));
const TorreyTrailPageRoute = lazy(() => import("./pages/TorreyTrailDetail.tsx"));

const Events = lazy(() => import("./pages/Events.tsx"));
const News = lazy(() => import("./pages/News.tsx"));
const Culture = lazy(() => import("./pages/Culture.tsx"));
const MuseumDetail = lazy(() => import("./pages/culture/MuseumDetail.tsx"));
const BreweryDetail = lazy(() => import("./pages/culture/BreweryDetail.tsx"));

const MapPage = lazy(() => import("./pages/MapPage.tsx"));
const Moon = lazy(() => import("./pages/Moon.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const Auth = lazy(() => import("./pages/Auth.tsx"));
const CasaBeach = lazy(() => import("./pages/CasaBeach.tsx"));
const HappeningStoryPage = lazy(() => import("./pages/HappeningStoryPage.tsx"));
const HappeningArchive = lazy(() => import("./pages/HappeningArchive.tsx"));

// Guides — single dynamic route; sitemap stays a dedicated page.
const GuidesIndex = lazy(() => import("./pages/guides/GuidesIndex.tsx"));
const GuideDetail = lazy(() => import("./pages/guides/GuideDetail.tsx"));
const GuidesSitemap = lazy(() => import("./pages/guides/GuidesSitemap.tsx"));
const CoastalLagoons = lazy(() => import("./pages/guides/CoastalLagoons.tsx"));
const LagoonDetail = lazy(() => import("./pages/guides/LagoonDetail.tsx"));
const NorthCountyWaterSports = lazy(() => import("./pages/guides/NorthCountyWaterSports.tsx"));
const CoastalBirdingTrails = lazy(() => import("./pages/guides/CoastalBirdingTrails.tsx"));
const ThingsToDoInLaJolla = lazy(() => import("./pages/guides/ThingsToDoInLaJolla.tsx"));
const ThingsToDoInPacificBeach = lazy(() => import("./pages/guides/ThingsToDoInPacificBeach.tsx"));
const ThingsToDoInMissionBeach = lazy(() => import("./pages/guides/ThingsToDoInMissionBeach.tsx"));
const ThingsToDoInOceanBeach = lazy(() => import("./pages/guides/ThingsToDoInOceanBeach.tsx"));
const BalboaParkMuseums = lazy(() => import("./pages/guides/BalboaParkMuseums.tsx"));
const BalboaParkMuseumsMap = lazy(() => import("./pages/guides/BalboaParkMuseumsMap.tsx"));
const BeachParkingMapPage = lazy(() => import("./pages/guides/BeachParkingMap.tsx"));

const LaJollaCoveSnorkeling = lazy(() => import("./pages/guides/LaJollaCoveSnorkeling.tsx"));
const DogFriendlyBeachesSanDiego = lazy(() => import("./pages/guides/DogFriendlyBeachesSanDiego.tsx"));
const OceanBeachDogBeachGuide = lazy(() => import("./pages/guides/OceanBeachDogBeachGuide.tsx"));
const ThingsToDoInOceanside = lazy(() => import("./pages/guides/ThingsToDoInOceanside.tsx"));
const SanDiegoSeaCaves = lazy(() => import("./pages/guides/SanDiegoSeaCaves.tsx"));
const MissionBaySanDiego = lazy(() => import("./pages/guides/MissionBaySanDiego.tsx"));
const SunsetCliffsTidePools = lazy(() => import("./pages/guides/SunsetCliffsTidePools.tsx"));
const BestBonfireBeachesSanDiego = lazy(() => import("./pages/guides/BestBonfireBeachesSanDiego.tsx"));
const BestBeachesInSanDiego = lazy(() => import("./pages/guides/BestBeachesInSanDiego.tsx"));
const BeachCamping = lazy(() => import("./pages/guides/BeachCamping.tsx"));

const MissionBeachBoardwalk = lazy(() => import("./pages/guides/MissionBeachBoardwalk.tsx"));
const MissionBeachCompleteGuide = lazy(() => import("./pages/guides/MissionBeachCompleteGuide.tsx"));
const OceanBeachCompleteGuide = lazy(() => import("./pages/guides/OceanBeachCompleteGuide.tsx"));
const CoastalBikingSanDiego = lazy(() => import("./pages/guides/CoastalBikingSanDiego.tsx"));
const SanDiegoBioluminescence = lazy(() => import("./pages/guides/SanDiegoBioluminescence.tsx"));
const TidePoolingCalendar = lazy(() => import("./pages/guides/TidePoolingCalendar.tsx"));
const TideCalendar = lazy(() => import("./pages/guides/TideCalendar.tsx"));
const BigSurfStormWatch = lazy(() => import("./pages/guides/BigSurfStormWatch.tsx"));
const KingTidesSanDiego = lazy(() => import("./pages/guides/KingTidesSanDiego.tsx"));
const MissionOceanPacificBeach = lazy(() => import("./pages/guides/MissionOceanPacificBeach.tsx"));
const NatureCalendar = lazy(() => import("./pages/NatureCalendar.tsx"));
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex.tsx"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost.tsx"));
const TorreyTrailDetail = lazy(() => import("./pages/blog/TorreyTrailDetail.tsx"));
const NewsletterConfirm = lazy(() => import("./pages/NewsletterConfirm.tsx"));
const Today = lazy(() => import("./pages/Today.tsx"));
const BeachToday = lazy(() => import("./pages/BeachToday.tsx"));
const RightNow = lazy(() => import("./pages/RightNow.tsx"));
const TideMonth = lazy(() => import("./pages/TideMonth.tsx"));
const WaterQuality = lazy(() => import("./pages/WaterQuality.tsx"));
const SiteIndex = lazy(() => import("./pages/SiteIndex.tsx"));
const SpottedToday = lazy(() => import("./pages/SpottedToday.tsx"));
const SandyShoes = lazy(() => import("./pages/SandyShoes.tsx"));

const queryClient = new QueryClient();

const RouteFallback = () => <div className="min-h-screen" aria-hidden />;

const App = () => {
  useImageProtection();
  return (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            {/* RouteCanonical removed — every page emits its own canonical via SeoHead / Helmet. Two canonicals confused crawlers. */}
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/today" element={<Today />} />
                <Route path="/right-now" element={<RightNow />} />
                <Route path="/water-quality" element={<WaterQuality />} />
                <Route path="/beaches" element={<Beaches />} />
                <Route path="/beaches/:slug" element={<BeachDetail />} />
                <Route path="/beaches/:slug/today" element={<BeachToday />} />
                <Route path="/casa-beach" element={<CasaBeach />} />
                <Route path="/surf" element={<Surf />} />
                <Route path="/tides" element={<Tides />} />
                <Route path="/tides/:station/:month" element={<TideMonth />} />
                <Route path="/weather" element={<Weather />} />
                <Route path="/dining" element={<Dining />} />
                <Route path="/nightlife" element={<Nightlife />} />
                <Route path="/activities" element={<Activities />} />
                <Route path="/trails" element={<Trails />} />
                <Route path="/trails/torrey-pines" element={<TorreyTrailsIndex />} />
                <Route path="/trails/torrey-pines/:slug" element={<TorreyTrailPageRoute />} />
                <Route path="/trails/:slug" element={<TrailDetail />} />

                <Route path="/events" element={<Events />} />
                <Route path="/news" element={<News />} />
                <Route path="/culture" element={<Culture />} />
                <Route path="/culture/museums/:slug" element={<MuseumDetail />} />
                <Route path="/culture/breweries/:slug" element={<BreweryDetail />} />
                
                <Route path="/map" element={<MapPage />} />
                <Route path="/moon" element={<Moon />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/guides" element={<GuidesIndex />} />
                <Route path="/guides/sitemap" element={<GuidesSitemap />} />
                <Route path="/guides/coastal-lagoons" element={<CoastalLagoons />} />
                <Route path="/guides/coastal-lagoons/:slug" element={<LagoonDetail />} />
                <Route path="/guides/north-county-water-sports" element={<NorthCountyWaterSports />} />
                <Route path="/guides/coastal-birding-trails" element={<CoastalBirdingTrails />} />
                <Route path="/guides/things-to-do-in-la-jolla" element={<ThingsToDoInLaJolla />} />
                <Route path="/guides/things-to-do-in-pacific-beach" element={<ThingsToDoInPacificBeach />} />
                <Route path="/guides/things-to-do-in-mission-beach" element={<ThingsToDoInMissionBeach />} />
                <Route path="/guides/things-to-do-in-ocean-beach" element={<ThingsToDoInOceanBeach />} />
                <Route path="/guides/balboa-park-museums" element={<BalboaParkMuseums />} />
                <Route path="/guides/balboa-park-museums-map" element={<BalboaParkMuseumsMap />} />
                <Route path="/guides/san-diego-beach-parking-map" element={<BeachParkingMapPage />} />

                <Route path="/guides/la-jolla-cove-snorkeling" element={<LaJollaCoveSnorkeling />} />
                <Route path="/guides/dog-friendly-beaches-san-diego" element={<DogFriendlyBeachesSanDiego />} />
                <Route path="/guides/ocean-beach-dog-beach-guide" element={<OceanBeachDogBeachGuide />} />
                <Route path="/guides/things-to-do-in-oceanside" element={<ThingsToDoInOceanside />} />
                <Route path="/guides/san-diego-sea-caves" element={<SanDiegoSeaCaves />} />
                <Route path="/guides/mission-bay-san-diego" element={<MissionBaySanDiego />} />
                <Route path="/guides/sunset-cliffs-tide-pools" element={<SunsetCliffsTidePools />} />
                <Route path="/guides/best-bonfire-beaches-san-diego" element={<BestBonfireBeachesSanDiego />} />
                <Route path="/guides/best-beaches-in-san-diego" element={<BestBeachesInSanDiego />} />
                <Route path="/guides/beach-camping-san-diego" element={<BeachCamping />} />
                
                <Route path="/guides/mission-beach-boardwalk" element={<MissionBeachBoardwalk />} />
                <Route path="/guides/mission-beach-complete-guide" element={<MissionBeachCompleteGuide />} />
                <Route path="/guides/ocean-beach-complete-guide" element={<OceanBeachCompleteGuide />} />
                <Route path="/guides/coastal-biking-san-diego" element={<CoastalBikingSanDiego />} />
                <Route path="/guides/san-diego-bioluminescence" element={<SanDiegoBioluminescence />} />
                <Route path="/guides/tide-pooling-calendar" element={<TidePoolingCalendar />} />
                <Route path="/guides/san-diego-tide-calendar" element={<TideCalendar />} />
                <Route path="/guides/san-diego-big-surf-guide" element={<BigSurfStormWatch />} />
                <Route path="/guides/king-tides-san-diego" element={<KingTidesSanDiego />} />
                <Route path="/guides/mission-beach-vs-ocean-beach-vs-pacific-beach" element={<MissionOceanPacificBeach />} />
                <Route path="/nature-calendar" element={<NatureCalendar />} />
                <Route path="/guides/:slug" element={<GuideDetail />} />
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/torrey-pines-hike-every-trail-ranked/:trailSlug" element={<TorreyTrailDetail />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
                <Route path="/happening/archive" element={<HappeningArchive />} />
                <Route path="/happening/:slug" element={<HappeningStoryPage />} />
                <Route path="/site-index" element={<SiteIndex />} />
                <Route path="/spotted-today" element={<SpottedToday />} />
                <Route path="/sandy-shoes" element={<SandyShoes />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <Suspense fallback={null}><CookieConsent /></Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
